import React, { useState, useMemo } from 'react';
import { Box, Typography, Divider, IconButton } from '@mui/material';
import { Add as AddIcon, AccessTime as AccessTimeIcon } from '@mui/icons-material';
import { SectionFieldDTO, FieldEntryDTO } from '../magic-report.type';
import { TextArrayFieldRenderer } from './field-renderers/TextArrayFieldRenderer';
import { useMagicReport } from '../context/MagicReportContext';
import { getNextActionItemOrder } from './action-items.util';
import { LAST_MONTH_TOGGLE_BUTTON_SX, DELETE_ICON_SPACER_WIDTH, BOOKMARK_ICON_SPACER_WIDTH } from './sections.style';
import { LastMonthReadonlyPanel } from './LastMonthFieldToggle';

interface CommentsSectionProps {
  commentsField: SectionFieldDTO;
  onChange: (updatedField: SectionFieldDTO) => void;
  fieldOptions?: string[];
  title?: string; // Optional custom title, defaults to formatted field name
  onBook?: () => number;
  showBookmark?: boolean; // Whether entries show the action-item bookmark (defaults to true)
}

/**
 * Reusable Comments Section component
 * Used across all Magic Report sections to render TextArray comment fields
 */
export const CommentsSection: React.FC<CommentsSectionProps> = ({
  commentsField,
  onChange,
  fieldOptions,
  title,
  onBook,
  showBookmark = true,
}) => {
  const { reportState } = useMagicReport();
  // Format field name if no custom title provided
  const displayTitle = title || formatFieldName(commentsField.fieldName);
  const [showLastMonth, setShowLastMonth] = useState(false);

  // Separate last month comments from current comments
  const { lastMonthEntries, currentEntries } = useMemo(() => {
    const entries = commentsField.entries || [];
    return {
      lastMonthEntries: entries.filter(entry => entry.isFromLastMonth === true),
      currentEntries: entries.filter(entry => !entry.isFromLastMonth),
    };
  }, [commentsField.entries]);

  const handleAddEntry = () => {
    const newEntry: FieldEntryDTO = {
      value: '',
      addToActionItems: false,
      order: commentsField.entries?.length || 0,
    };
    onChange({
      ...commentsField,
      entries: [...(commentsField.entries || []), newEntry],
    });
  };

  const handleToggleShowLastMonth = () => {
    setShowLastMonth(!showLastMonth);
  };

  return (
    <Box>
      <Divider sx={{ mb: 1 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Typography variant='body2' color='text' fontWeight='medium' textTransform='capitalize'>
          {displayTitle}
        </Typography>
        <IconButton
          onClick={handleAddEntry}
          size='small'
          sx={{
            border: '1px dashed',
            borderColor: 'divider',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: 'primary.main',
              color: 'primary.main',
              transform: 'scale(1.1)',
            },
          }}
        >
          <AddIcon fontSize='small' />
        </IconButton>
        {lastMonthEntries.length > 0 && (
          <Box
            onClick={handleToggleShowLastMonth}
            sx={{
              ...LAST_MONTH_TOGGLE_BUTTON_SX,
              color: showLastMonth ? 'primary.main' : 'text.secondary',
              '&:hover': {
                backgroundColor: 'transparent',
                color: showLastMonth ? 'primary.dark' : 'text.primary',
              },
            }}
          >
            <AccessTimeIcon
              sx={{
                width: '0.8rem',
                height: '0.8rem',
              }}
            />
            <Typography
              variant='caption'
              sx={{
                fontWeight: 600,
                textTransform: 'none',
                letterSpacing: 0.2,
                color: 'inherit',
              }}
            >
              {showLastMonth ? 'Hide last month' : 'Show last month'}
            </Typography>
          </Box>
        )}
      </Box>
      <Box>
        {/* Show last month comments at the top when toggled */}
        {showLastMonth && lastMonthEntries.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {lastMonthEntries.map((entry, index) => (
              <Box
                key={`last-month-${index}`}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  mb: 2,
                }}
              >
                <Box sx={{ flexGrow: 1, position: 'relative' }}>
                  <LastMonthReadonlyPanel value={entry.value || ''} />
                </Box>

                {/* Invisible spacers to match [delete] [bookmark] order in TextArrayFieldRenderer */}
                <Box sx={{ width: DELETE_ICON_SPACER_WIDTH, flexShrink: 0, pt: 0.75, visibility: 'hidden' }} />
                <Box sx={{ width: BOOKMARK_ICON_SPACER_WIDTH, flexShrink: 0, pt: 0.75 }} />
              </Box>
            ))}
          </Box>
        )}
        {/* Current comments */}
        <TextArrayFieldRenderer
          entries={currentEntries}
          onChange={entries => {
            // Merge current entries with last month entries when updating
            const allEntries = [...lastMonthEntries, ...entries];
            onChange({ ...commentsField, entries: allEntries });
          }}
          fieldName={commentsField.fieldName}
          showAddButton={false}
          getNextActionItemOrder={onBook ?? (() => getNextActionItemOrder(reportState.sections))}
          showBookmark={showBookmark}
        />
      </Box>
    </Box>
  );
};

// Helper function to format field names
const formatFieldName = (fieldName: string): string => {
  // Convert camelCase or PascalCase to Title Case with spaces
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};
